import React from 'react'

import { Modal } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func, string, oneOf } from 'prop-types'

import Loader from 'Pods/Shared/Loader'

import { DAYS_TOTAL, METRIC_TYPES, CHART_COLORS } from './constants/chartConstants'
import useItemPerformance from './hooks/useItemPerformance'
import PerformanceChart from './PerformanceChart'
import PerformanceModalMobile from './PerformanceModalMobile'
import { formatMetricValue } from './utils/formatters'
import isMobileDevice from '../../utils/isMobileDevice'

function calculateTotals(data) {
  const values = Object.values(data)
  const total = values.reduce((sum, val) => sum + val, 0)
  const average = values.length > 0 ? total / values.length : 0
  return { total, average }
}

function PerformanceModal({ itemId, itemTitle, metricType, onClose }) {
  const isMobile = isMobileDevice()
  const { data, isLoading, error } = useItemPerformance(itemId, metricType)

  const isEarnings = metricType === METRIC_TYPES.earnings
  const chartData = data?.series || {}
  const boostPeriods = data?.boostPeriods || []
  const { total, average } = calculateTotals(chartData)

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="performance-modal__loader">
          <Loader />
        </div>
      )
    }

    if (error) {
      return <div className="performance-modal__error">{error}</div>
    }

    const unit = isEarnings ? '' : ' views'

    return (
      <>
        <div className="performance-modal__stats">
          <div className="performance-modal__stat">
            <span className="performance-modal__stat-value">
              {formatMetricValue(total, metricType)}
            </span>
            <span className="performance-modal__stat-label">
              {unit} total in last {DAYS_TOTAL} days
            </span>
          </div>
          <div className="performance-modal__stat">
            <span className="performance-modal__stat-value">
              {formatMetricValue(average, metricType)}
            </span>
            <span className="performance-modal__stat-label">{unit} per day</span>
          </div>
        </div>

        <PerformanceChart data={chartData} boostPeriods={boostPeriods} metricType={metricType} />

        {boostPeriods.length > 0 && (
          <div className="performance-modal__legend">
            <span className="performance-modal__legend-item">
              <span
                className="performance-modal__legend-dot"
                style={{ backgroundColor: CHART_COLORS.normal }}
              />
              Regular
            </span>
            <span className="performance-modal__legend-item">
              <span
                className="performance-modal__legend-dot"
                style={{ backgroundColor: CHART_COLORS.boost }}
              />
              During Sales Boost
            </span>
          </div>
        )}
      </>
    )
  }

  const title = `${itemTitle} - ${isEarnings ? 'Earnings' : 'Views'}`

  if (isMobile) {
    return (
      <PerformanceModalMobile title={title} onClose={onClose}>
        {renderContent()}
      </PerformanceModalMobile>
    )
  }

  return (
    <Modal
      size="medium"
      header={<span className="cgt-text-medium cgt-text-medium--semi-bold">{title}</span>}
      onClose={onClose}
      canClose
    >
      {renderContent()}
    </Modal>
  )
}

PerformanceModal.propTypes = {
  itemId: string.isRequired,
  itemTitle: string.isRequired,
  metricType: oneOf([METRIC_TYPES.views, METRIC_TYPES.earnings]).isRequired,
  onClose: func.isRequired,
}

export default PerformanceModal
